import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { addQueryWithReplace } from 'Helpers/Query';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';


const GoalStrategiesDetailsContext = React.createContext(undefined);

export const GoalStrategiesDetailsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [collection, setCollection] = useState(undefined);
  const { displayErrorMessage } = useSnackbarContext();
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const queryGoalId = searchParams.get('currentGoalId');

  const handleUpdateGoalParam = (goalId) => {
    const newParams = new URLSearchParams(search);
    newParams.set('currentGoalId', goalId);
    history.push({
      search: newParams.toString(),
    });
  };

  const fetchGoalDetails = useCallback(async (rel) => {
    setLoading(true);
    try {
      const resp = await rel.link('related').get();
      setCollection(resp);

      // If we do not have a goalId in the query params default to the first index
      if (!queryGoalId) {
        addQueryWithReplace({ currentGoalId: resp.resources().head().id() });
      }
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLoading(false);
    }
  }, [queryGoalId]);

  const currentGoalIdx = useMemo(() => (collection && queryGoalId
    ? collection?.resources().list().findIndex((g) => g.id() === queryGoalId)
    : 0), [collection, queryGoalId]);

  const handleSetGoal = useCallback((idx) => {
    if (collection) {
      // Find the desired goal in the list based on the index and update the query accordingly
      const nextGoalId = collection.resources().list()[idx].id();
      handleUpdateGoalParam(nextGoalId);
    }
  }, [collection, currentGoalIdx]);


  const memoizedValue = React.useMemo(() => {
    return {
      collection,
      fetchGoalDetails,
      loading,
      currentGoalIdx,
      handleSetGoal,
      isLastGoal: collection && currentGoalIdx === collection?.resources().length() - 1,
    };
  }, [
    collection,
    fetchGoalDetails,
    loading,
    currentGoalIdx,
    handleSetGoal,
  ]);

  return (
    <GoalStrategiesDetailsContext.Provider value={memoizedValue}>
      {children}
    </GoalStrategiesDetailsContext.Provider>
  );
};

GoalStrategiesDetailsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export const useGoalStrategiesDetailsContext = () => {
  const context = React.useContext(GoalStrategiesDetailsContext);

  if (context === undefined) {
    throw new Error('useGoalStrategiesDetails must be used within a GoalStrategiesDetailsProvider');
  }

  return context;
};
